import React from 'react';
import { useMutation } from '@tanstack/react-query';

import {
  handlerEventsCleanupApi,
  workerEventsCleanupApi,
  workflowDataCleanupApi,
  type HandlerEventsCleanupResponse,
  type WorkerEventsCleanupResponse,
  type WorkflowDataCleanupResponse,
  type WorkflowDataCleanupTargets,
} from '../api/cleanupApi';
import { fetchHandlersV2Api, type HandlerSpecRow } from '../api/handlersV2Api';
import { fetchWorkersV2Api, type Worker } from '../api/workersApi';

function asOptionalTrimmed(v: string): string | undefined {
  const s = String(v ?? '').trim();
  return s ? s : undefined;
}

const DEFAULT_TARGETS: Required<WorkflowDataCleanupTargets> = {
  runs: false,
  steps: false,
  executions: false,
  outcomes: true,
  run_outcomes: false,
  scopes: false,
};

export default function WorkflowDataCleanupPanel() {
  const [workerKey, setWorkerKey] = React.useState('system');
  const [system, setSystem] = React.useState('');
  const [handlerKeyBase, setHandlerKeyBase] = React.useState('');
  const [handlerNamespace, setHandlerNamespace] = React.useState('');
  const [scopeNamespace, setScopeNamespace] = React.useState('');
  const [scopeKey, setScopeKey] = React.useState('');

  const [entityResults, setEntityResults] = React.useState<HandlerSpecRow[] | null>(null);
  const [entityWorkerResults, setEntityWorkerResults] = React.useState<Worker[] | null>(null);
  const [lastEntityDelete, setLastEntityDelete] = React.useState<HandlerEventsCleanupResponse | null>(null);
  const [lastWorkerProjectionDelete, setLastWorkerProjectionDelete] = React.useState<WorkerEventsCleanupResponse | null>(null);
  const [lastWorkerRuntimeDelete, setLastWorkerRuntimeDelete] = React.useState<WorkflowDataCleanupResponse | null>(null);

  const [targets, setTargets] = React.useState<Required<WorkflowDataCleanupTargets>>(DEFAULT_TARGETS);

  const [confirmText, setConfirmText] = React.useState('');
  const [lastPreview, setLastPreview] = React.useState<WorkflowDataCleanupResponse | null>(null);

  const previewMutation = useMutation({
    mutationFn: async () => {
      return workflowDataCleanupApi({
        dry_run: true,
        filters: {
          worker_key: asOptionalTrimmed(workerKey),
          system: asOptionalTrimmed(system),
          handler_key_base: asOptionalTrimmed(handlerKeyBase),
          handler_namespace: asOptionalTrimmed(handlerNamespace),
          scope_namespace: asOptionalTrimmed(scopeNamespace),
          scope_key: asOptionalTrimmed(scopeKey),
        },
        targets,
      });
    },
    onSuccess: (data) => {
      setLastPreview(data);
    },
  });

  const executeMutation = useMutation({
    mutationFn: async () => {
      return workflowDataCleanupApi({
        dry_run: false,
        filters: {
          worker_key: asOptionalTrimmed(workerKey),
          system: asOptionalTrimmed(system),
          handler_key_base: asOptionalTrimmed(handlerKeyBase),
          handler_namespace: asOptionalTrimmed(handlerNamespace),
          scope_namespace: asOptionalTrimmed(scopeNamespace),
          scope_key: asOptionalTrimmed(scopeKey),
        },
        targets,
      });
    },
    onSuccess: (data) => {
      setLastPreview(data);
      setConfirmText('');
    },
  });

  const entitySearchMutation = useMutation({
    mutationFn: async () => {
      // Fetch a larger window and filter client-side.
      // IMPORTANT: cleanup tooling must operate on v2 projection entities only.
      // Do not merge legacy handler-registry rows here.
      const [handlersRes, workersRes] = await Promise.all([
        fetchHandlersV2Api({ limit: 2000, registry_fallback: false }),
        fetchWorkersV2Api({ limit: 2000 }),
      ]);
      const handlerItems = handlersRes.items ?? [];
      const workerItems = workersRes.items ?? [];

      const wk = asOptionalTrimmed(workerKey);
      const sys = asOptionalTrimmed(system);
      const kb = asOptionalTrimmed(handlerKeyBase);
      const ns = asOptionalTrimmed(handlerNamespace);
      const sysPrefix = sys ? `${sys}.` : '';

      const filteredHandlers = handlerItems.filter((h) => {
        if (wk && String(h.worker_key ?? '').trim() !== wk) return false;
        if (sys) {
          const base = String(h.handler_key_base ?? '').trim();
          const namespace = String(h.handler_namespace ?? '').trim();
          const baseOk = base ? base.startsWith(sysPrefix) : false;
          const nsOk = namespace ? namespace.startsWith(sysPrefix) : false;
          if (!baseOk && !nsOk) return false;
        }
        if (kb && String(h.handler_key_base ?? '').trim() !== kb) return false;
        if (ns && String(h.handler_namespace ?? '').trim() !== ns) return false;
        return true;
      });

      // Workers do not have system/handler filters; only filter by worker_key.
      const filteredWorkers = workerItems.filter((w) => {
        if (wk && String(w.worker_key ?? '').trim() !== wk) return false;
        return true;
      });

      return { handlers: filteredHandlers, workers: filteredWorkers };
    },
    onSuccess: (data) => {
      setEntityResults(data.handlers);
      setEntityWorkerResults(data.workers);
      setLastEntityDelete(null);
      setLastWorkerProjectionDelete(null);
      setLastWorkerRuntimeDelete(null);
    },
  });

  const workerDeleteMutation = useMutation({
    mutationFn: async (wk: string) => {
      const [projection, runtime] = await Promise.all([
        // Remove the outcomes that power /workers/v2 (system worker-management outputs.worker events).
        workerEventsCleanupApi({ dry_run: false, worker_key: wk }),
        // Purge workflow runtime data for this worker_key (runs/steps/executions/outcomes/run_outcomes) using selected Targets.
        workflowDataCleanupApi({
          dry_run: false,
          filters: {
            worker_key: asOptionalTrimmed(wk),
            system: asOptionalTrimmed(system),
            handler_key_base: asOptionalTrimmed(handlerKeyBase),
            handler_namespace: asOptionalTrimmed(handlerNamespace),
          },
          targets,
        }),
      ]);
      return { projection, runtime };
    },
    onSuccess: (data) => {
      setLastWorkerProjectionDelete(data.projection);
      setLastWorkerRuntimeDelete(data.runtime);
      // Refresh the projection lists so the deleted worker disappears from /workers/v2.
      entitySearchMutation.mutate();
    },
  });

  const entityDeleteMutation = useMutation({
    mutationFn: async (handler_key: string) => {
      return handlerEventsCleanupApi({ dry_run: false, handler_key });
    },
    onSuccess: (resp) => {
      setLastEntityDelete(resp);
      // Refresh list after deletion.
      entitySearchMutation.mutate();
    },
  });

  const anyFilter = !!(asOptionalTrimmed(workerKey) || asOptionalTrimmed(system) || asOptionalTrimmed(handlerKeyBase) || asOptionalTrimmed(handlerNamespace));
  const anyScopeFilter = !!(asOptionalTrimmed(scopeNamespace) || asOptionalTrimmed(scopeKey));
  const anyFilterOrScope = anyFilter || anyScopeFilter;
  const anyTarget = Object.values(targets).some(Boolean);
  const canPreview = anyFilterOrScope && anyTarget && !previewMutation.isPending && !executeMutation.isPending;
  const canExecute =
    !!lastPreview &&
    confirmText.trim().toUpperCase() === 'DELETE' &&
    !previewMutation.isPending &&
    !executeMutation.isPending;

  const canDeleteWorker =
    confirmText.trim().toUpperCase() === 'DELETE'
    && anyTarget
    && !workerDeleteMutation.isPending
    && !previewMutation.isPending
    && !executeMutation.isPending;

  const errorMsg =
    (previewMutation.error as any)?.message
    || (executeMutation.error as any)?.message
    || (entitySearchMutation.error as any)?.message
    || (entityDeleteMutation.error as any)?.message
    || (workerDeleteMutation.error as any)?.message;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Workflow Data Cleanup</h2>
        <p className="text-xs text-neutral-500">
          Preview and delete workflow lifecycle data by AND-combined filters (tenant-scoped). This is irreversible.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-neutral-800 rounded p-3 space-y-3">
          <div className="text-sm font-medium text-neutral-200">Filters (AND)</div>

          <LabeledInput label="worker_key" value={workerKey} onChange={setWorkerKey} placeholder="e.g. system" />
          <LabeledInput label="system" value={system} onChange={setSystem} placeholder="e.g. system" />
          <LabeledInput label="handler_key_base" value={handlerKeyBase} onChange={setHandlerKeyBase} placeholder="e.g. worker" />
          <LabeledInput label="handler_namespace" value={handlerNamespace} onChange={setHandlerNamespace} placeholder="e.g. create" />
          <LabeledInput label="scope_namespace" value={scopeNamespace} onChange={setScopeNamespace} placeholder="e.g. system.worker" />
          <LabeledInput label="scope_key" value={scopeKey} onChange={setScopeKey} placeholder="e.g. test123" />

          {!anyFilterOrScope && (
            <div className="text-xs text-amber-300">
              Provide at least one filter.
            </div>
          )}
        </div>

        <div className="border border-neutral-800 rounded p-3 space-y-3">
          <div className="text-sm font-medium text-neutral-200">Targets</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <Checkbox label="outcomes" checked={targets.outcomes} onChange={(v) => setTargets((t) => ({ ...t, outcomes: v }))} />
            <Checkbox label="executions" checked={targets.executions} onChange={(v) => setTargets((t) => ({ ...t, executions: v }))} />
            <Checkbox label="steps" checked={targets.steps} onChange={(v) => setTargets((t) => ({ ...t, steps: v }))} />
            <Checkbox label="run_outcomes" checked={targets.run_outcomes} onChange={(v) => setTargets((t) => ({ ...t, run_outcomes: v }))} />
            <Checkbox label="runs" checked={targets.runs} onChange={(v) => setTargets((t) => ({ ...t, runs: v }))} />
            <Checkbox label="scopes" checked={targets.scopes} onChange={(v) => setTargets((t) => ({ ...t, scopes: v }))} />
          </div>

          {!anyTarget && (
            <div className="text-xs text-amber-300">
              Select at least one target.
            </div>
          )}

          <div className="text-xs text-neutral-500">
            Note: deleting runs implies steps + executions + outcomes + run_outcomes. Deleting steps implies executions + outcomes. Deleting executions implies outcomes. Scopes deletes workflow_scopes entries for matched runs (only when not used by other runs).
          </div>
        </div>
      </div>

      <div className="border border-neutral-800 rounded p-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-medium text-neutral-200">Search for entities…</div>
            <div className="text-xs text-neutral-500">Uses the same filters above to locate handler catalog entries.</div>
          </div>
          <button
            className={
              'px-3 py-2 rounded border text-sm ' +
              (anyFilter && !entitySearchMutation.isPending && !entityDeleteMutation.isPending
                ? 'border-neutral-700 hover:border-neutral-500 text-neutral-200'
                : 'border-neutral-900 text-neutral-600 cursor-not-allowed')
            }
            disabled={!anyFilter || entitySearchMutation.isPending || entityDeleteMutation.isPending}
            onClick={() => entitySearchMutation.mutate()}
          >
            {entitySearchMutation.isPending ? 'Searching…' : 'Search entities'}
          </button>
        </div>

        {entityResults !== null && entityWorkerResults !== null && (
          <div className="space-y-2">
            <div className="text-xs text-neutral-500">Matched handlers: {entityResults.length}</div>

            {(() => {
              const keys = Array.from(new Set((entityWorkerResults ?? []).map((w) => String(w.worker_key ?? '').trim()).filter(Boolean)))
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

              return (
                <div className="space-y-2">
                  <div className="text-xs text-neutral-500">Matched worker keys: {keys.length}</div>

                  {lastWorkerProjectionDelete && (
                    <div className="text-xs text-neutral-400">
                      Deleted worker projection events: matched={lastWorkerProjectionDelete.matched} deleted={lastWorkerProjectionDelete.deleted}
                    </div>
                  )}

                  {lastWorkerRuntimeDelete && (
                    <div className="text-xs text-neutral-400">
                      Deleted worker runtime data: matched runs={lastWorkerRuntimeDelete.matched.runs} steps={lastWorkerRuntimeDelete.matched.steps} executions={lastWorkerRuntimeDelete.matched.executions} outcomes={lastWorkerRuntimeDelete.matched.outcomes} run_outcomes={lastWorkerRuntimeDelete.matched.run_outcomes} scopes={lastWorkerRuntimeDelete.matched.scopes} · deleted runs={lastWorkerRuntimeDelete.deleted.runs} steps={lastWorkerRuntimeDelete.deleted.steps} executions={lastWorkerRuntimeDelete.deleted.executions} outcomes={lastWorkerRuntimeDelete.deleted.outcomes} run_outcomes={lastWorkerRuntimeDelete.deleted.run_outcomes} scopes={lastWorkerRuntimeDelete.deleted.scopes}
                    </div>
                  )}

                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-neutral-500 border-b border-neutral-800">
                          <th className="text-left py-2 pr-2">worker_key</th>
                          <th className="text-right py-2">action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map((k) => (
                          <tr key={k} className="border-b border-neutral-900">
                            <td className="py-2 pr-2 text-neutral-200 break-all">{k}</td>
                            <td className="py-2 text-right">
                              <button
                                className={
                                  'px-3 py-1 rounded border text-xs ' +
                                  (canDeleteWorker
                                    ? 'border-rose-600 text-rose-200 hover:border-rose-500'
                                    : 'border-neutral-900 text-neutral-600 cursor-not-allowed')
                                }
                                disabled={!canDeleteWorker}
                                onClick={() => workerDeleteMutation.mutate(k)}
                                title="Deletes workflow runtime data for this worker_key using the selected Targets"
                              >
                                {workerDeleteMutation.isPending ? 'Deleting…' : 'Delete worker'}
                              </button>
                            </td>
                          </tr>
                        ))}
                        {keys.length === 0 && (
                          <tr>
                            <td colSpan={2} className="py-3 text-xs text-neutral-500">No matching worker keys.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {lastEntityDelete && (
              <div className="text-xs text-neutral-400">
                Deleted projection events: matched={lastEntityDelete.matched} deleted={lastEntityDelete.deleted}
              </div>
            )}

            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-neutral-500 border-b border-neutral-800">
                    <th className="text-left py-2 pr-2">handler_key</th>
                    <th className="text-left py-2 pr-2">updated</th>
                    <th className="text-right py-2">action</th>
                  </tr>
                </thead>
                <tbody>
                  {entityResults.map((h) => (
                    <tr key={h.handler_key} className="border-b border-neutral-900">
                      <td className="py-2 pr-2 text-neutral-200 break-all">{h.handler_key}</td>
                      <td className="py-2 pr-2 text-neutral-400 whitespace-nowrap">{h.updated_at ? String(h.updated_at) : '—'}</td>
                      <td className="py-2 text-right">
                        <button
                          className={
                            'px-3 py-1 rounded border text-xs ' +
                            (confirmText.trim().toUpperCase() === 'DELETE' && !entityDeleteMutation.isPending
                              ? 'border-rose-600 text-rose-200 hover:border-rose-500'
                              : 'border-neutral-900 text-neutral-600 cursor-not-allowed')
                          }
                          disabled={confirmText.trim().toUpperCase() !== 'DELETE' || entityDeleteMutation.isPending}
                          onClick={() => entityDeleteMutation.mutate(h.handler_key)}
                          title="Deletes the outcomes-projected handler catalog entry (does not delete the handler-registry row)"
                        >
                          {entityDeleteMutation.isPending ? 'Deleting…' : 'Delete handler'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {entityResults.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-3 text-xs text-neutral-500">No matching handlers.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="text-xs text-neutral-500">
              Tip: set Targets to outcomes+steps+executions+runs to purge runtime data; use Delete handler to remove just the Handler Catalog projection.
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          className={
            'px-3 py-2 rounded border text-sm ' +
            (canPreview ? 'border-neutral-700 hover:border-neutral-500 text-neutral-200' : 'border-neutral-900 text-neutral-600 cursor-not-allowed')
          }
          disabled={!canPreview}
          onClick={() => previewMutation.mutate()}
        >
          {previewMutation.isPending ? 'Previewing…' : 'Preview'}
        </button>

        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500">Type</span>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            className="px-2 py-2 rounded pac-glass-subtle text-sm text-neutral-200 placeholder:text-neutral-700"
          />
        </div>

        <button
          className={
            'px-3 py-2 rounded border text-sm ' +
            (canExecute ? 'border-rose-600 text-rose-200 hover:border-rose-500' : 'border-neutral-900 text-neutral-600 cursor-not-allowed')
          }
          disabled={!canExecute}
          onClick={() => executeMutation.mutate()}
        >
          {executeMutation.isPending ? 'Deleting…' : 'Execute Delete'}
        </button>

        {!lastPreview && (
          <span className="text-xs text-neutral-500">Run Preview before deleting.</span>
        )}
      </div>

      {errorMsg && (
        <div className="text-sm text-rose-400 border border-rose-900/50 bg-rose-950/20 rounded p-3">
          {String(errorMsg)}
        </div>
      )}

      {lastPreview && (
        <div className="border border-neutral-800 rounded p-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-medium text-neutral-200">Result</div>
            <div className="text-xs text-neutral-500">
              {lastPreview.dry_run ? 'dry_run' : 'executed'} · tenant_id={lastPreview.tenant_id}
            </div>
          </div>

          <CountsTable title="Matched" counts={lastPreview.matched} />
          <CountsTable title="Deleted" counts={lastPreview.deleted} />

          <div className="text-xs text-neutral-500">
            Effective targets: {Object.entries(lastPreview.effective_targets).filter(([, v]) => v).map(([k]) => k).join(', ') || '—'}
          </div>
        </div>
      )}
    </div>
  );
}

function LabeledInput(props: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <div className="text-xs text-neutral-500 mb-1">{props.label}</div>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="w-full px-2 py-2 rounded pac-glass-subtle text-sm text-neutral-200 placeholder:text-neutral-700"
      />
    </label>
  );
}

function Checkbox(props: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 select-none">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
        className="accent-emerald-500"
      />
      <span className="text-neutral-200">{props.label}</span>
    </label>
  );
}

function CountsTable(props: { title: string; counts: { runs: number; steps: number; executions: number; outcomes: number; run_outcomes: number; scopes: number } }) {
  const c = props.counts;
  return (
    <div className="space-y-1">
      <div className="text-xs text-neutral-500">{props.title}</div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-sm">
        <CountChip label="runs" value={c.runs} />
        <CountChip label="steps" value={c.steps} />
        <CountChip label="executions" value={c.executions} />
        <CountChip label="outcomes" value={c.outcomes} />
        <CountChip label="run_outcomes" value={c.run_outcomes} />
        <CountChip label="scopes" value={c.scopes} />
      </div>
    </div>
  );
}

function CountChip(props: { label: string; value: number }) {
  return (
    <div className="rounded border border-neutral-800 px-2 py-1">
      <div className="text-[11px] text-neutral-500">{props.label}</div>
      <div className="text-sm text-neutral-200 tabular-nums">{Number(props.value ?? 0).toLocaleString()}</div>
    </div>
  );
}
